import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Clock, Tag } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const ContentBlock = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return (
        <h2 className="text-2xl md:text-3xl font-black text-white mt-12 mb-4">
          {block.text}
        </h2>
      );
    case 'p':
      return (
        <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-5">
          {block.text}
        </p>
      );
    case 'quote':
      return (
        <blockquote className="border-l-4 border-orange-500 pl-6 my-10">
          <p className="text-xl md:text-2xl font-black text-white leading-snug italic">
            "{block.text}"
          </p>
        </blockquote>
      );
    case 'stat':
      return (
        <div className="my-10 flex items-start gap-5 p-6 bg-gray-900 border border-gray-800 rounded-2xl">
          <span className="text-4xl md:text-5xl font-black text-orange-500 tracking-tight leading-none shrink-0">
            {block.value}
          </span>
          <span className="text-gray-400 text-sm leading-relaxed pt-1">{block.label}</span>
        </div>
      );
    default:
      return null;
  }
};

export default function BlogPost() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/meet-Joseph" replace />;

  const currentIndex = blogPosts.indexOf(post);
  const nextPost = blogPosts[(currentIndex + 1) % blogPosts.length];

  return (
    <div className="min-h-screen bg-gray-950 overflow-x-hidden">
      <Navigation isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} onNavClick={() => {}} activeSection="" />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-44 md:pb-16 px-6 border-b border-gray-800/50">
        <div className="container mx-auto max-w-3xl">

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
            <Link
              to="/meet-Joseph"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-orange-500 text-sm font-medium transition-colors mb-10"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Joseph
            </Link>
          </motion.div>

          <motion.div
            className="flex flex-wrap items-center gap-3 mb-6"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
          >
            <span className="flex items-center gap-1.5 text-[11px] text-gray-500 font-mono">
              <Clock className="w-3 h-3" /> {post.readTime}
            </span>
            <span className="w-px h-3 bg-gray-700" />
            <span className="text-[11px] text-gray-500 font-mono">{post.date}</span>
            <span className="w-px h-3 bg-gray-700" />
            <div className="flex gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 text-[10px] text-orange-500/70 border border-orange-500/20 px-2 py-0.5 rounded-md"
                >
                  <Tag className="w-2.5 h-2.5" /> {tag}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="text-4xl md:text-6xl font-black text-white tracking-tight leading-tight mb-3"
          >
            {post.title}
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="text-lg text-orange-500/80 font-medium"
          >
            {post.subtitle}
          </motion.p>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.07 } } }}
          >
            {post.content.map((block, i) => (
              <motion.div key={i} variants={fadeUp} custom={i * 0.3}>
                <ContentBlock block={block} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Author card */}
      <section className="px-6 pb-10">
        <div className="container mx-auto max-w-3xl">
          <div className="flex items-center gap-4 p-6 bg-gray-900 border border-gray-800 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
              <span className="text-orange-500 font-black text-sm">JM</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm">Joseph Macharia</p>
              <p className="text-gray-500 text-xs">Senior Software Engineer · Nairobi, Kenya</p>
            </div>
            <a
              href="https://linkedin.com/in/j-macharia"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 flex items-center gap-1.5 text-orange-500 text-sm font-bold hover:text-orange-400 transition-colors"
            >
              Connect <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* Next post */}
      <section className="px-6 pb-16 md:pb-24">
        <div className="container mx-auto max-w-3xl">
          <p className="text-[10px] uppercase tracking-widest text-gray-600 font-bold mb-4">Next post</p>
          <Link
            to={`/meet-Joseph/blog/${nextPost.slug}`}
            className="group flex items-start justify-between gap-6 p-6 md:p-8 bg-gray-900 border border-gray-800 rounded-2xl hover:border-orange-500/40 transition-all"
          >
            <div className="min-w-0">
              <h3 className="text-lg font-black text-white group-hover:text-orange-400 transition-colors mb-1 truncate">
                {nextPost.title}
              </h3>
              <p className="text-sm text-gray-500 truncate">{nextPost.subtitle}</p>
            </div>
            <ArrowUpRight className="w-5 h-5 text-gray-600 group-hover:text-orange-500 transition-colors shrink-0 mt-0.5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
